<template>
  <div ref="wrapper" class="tooltip-wrapper" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <slot />
    <transition name="tooltip-fade" @after-enter="positionTooltip">
      <teleport to="body">
        <div
          v-if="visible"
          ref="tooltip"
          class="tooltip-box max-w-fit min-w-fit"
          :class="placement"
          :style="tooltipStyles"
        >
          {{ content }}
        </div>
      </teleport>
    </transition>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  /** Text or HTML to show inside the tooltip */
  content: {
    type: String,
    required: true,
  },
  /** Tooltip placement: top, bottom, left, right */
  placement: {
    type: String,
    default: 'top',
    validator: (val) => ['top', 'bottom', 'left', 'right'].includes(val),
  },
  /** Delay in ms before showing tooltip */
  enterDelay: {
    type: Number,
    default: 100,
  },
  /** Delay in ms before hiding tooltip */
  leaveDelay: {
    type: Number,
    default: 100,
  },
});

const wrapper = ref(null);
const tooltip = ref(null);
const visible = ref(false);
const tooltipStyles = ref({});
let showTimer = null;
let hideTimer = null;

async function onMouseEnter() {
  clearTimeout(hideTimer);
  showTimer = setTimeout(async () => {
    visible.value = true;
    await nextTick();
    positionTooltip();
  }, props.enterDelay);
}

function onMouseLeave() {
  clearTimeout(showTimer);
  hideTimer = setTimeout(() => {
    visible.value = false;
  }, props.leaveDelay);
}

onBeforeUnmount(() => {
  clearTimeout(showTimer);
  clearTimeout(hideTimer);
});

function positionTooltip() {
  const wrapRect = wrapper.value.getBoundingClientRect();
  const tipRect = tooltip.value.getBoundingClientRect();
  let top = 0,
    left = 0;

  switch (props.placement) {
    case 'top':
      top = wrapRect.top - tipRect.height - 6;
      left = wrapRect.left + wrapRect.width / 2 - tipRect.width / 2;
      break;
    case 'bottom':
      top = wrapRect.bottom + 6;
      left = wrapRect.left + wrapRect.width / 2 - tipRect.width / 2;
      break;
    case 'left':
      top = wrapRect.top + wrapRect.height / 2 - tipRect.height / 2;
      left = wrapRect.left - tipRect.width - 6;
      break;
    case 'right':
      top = wrapRect.top + wrapRect.height / 2 - tipRect.height / 2;
      left = wrapRect.right + 6;
      break;
  }

  tooltipStyles.value = {
    position: 'absolute',
    top: `${top + window.scrollY}px`,
    left: `${left + window.scrollX}px`,
    zIndex: '9999',
  };
}
</script>

<style scoped>
.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip-box {
  padding: 6px 10px;
  background: black;
  color: #fff;
  border-radius: 4px;
  white-space: nowrap;
  font-size: 12px;
  z-index: 999 !important;
  pointer-events: none;
}
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.1s;
}
.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
.tooltip-fade-enter-to,
.tooltip-fade-leave-from {
  opacity: 1;
}
</style>
